/** Eight certificate-depth examples per part for Systems of Linear Equations. */

export const LA_SYSTEMS_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Two equations, unique solution",
    setup: "Solve $2x+3y=8$, $x-y=-1$ by elimination.",
    steps: [
      { text: "Augmented matrix $\\left(\\begin{array}{cc|c}2&3&8\\\\1&-1&-1\\end{array}\\right)$.", why: "Elimination works on the augmented array." },
      { text: "Swap rows so the pivot is $1$: $\\left(\\begin{array}{cc|c}1&-1&-1\\\\2&3&8\\end{array}\\right)$.", why: "Avoids fractions." },
      { text: "$R_2\\to R_2-2R_1$: $(0,5\\mid 10)$.", why: "Eliminate $x$ from the second row." },
      { text: "So $5y=10$, giving $y=2$.", why: "Back-substitution starts at the bottom." },
      { text: "First row: $x-y=-1$.", why: "Return to the top equation." },
      { text: "$x=y-1=1$.", why: "Substitute $y=2$." },
      { text: "Solution $(x,y)=(1,2)$.", why: "One pivot per unknown, so it is unique." },
      { text: "Rank $=2$ equals the number of unknowns, hence no free variables.", why: "Structural reason for uniqueness." },
    ],
    result: "$(x,y)=(1,2)$",
    check: "$2(1)+3(2)=8$ and $1-2=-1$. Both hold.",
    mistake: "Adding rows without matching the coefficient you intend to cancel.",
    life: "Balancing two blended fuels to hit a target octane and a target cost is exactly this $2\\times2$ system — the pivot tells you the blend is uniquely determined.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Inconsistent system",
    setup: "Solve $x+2y=3$, $2x+4y=7$.",
    steps: [
      { text: "Augmented matrix $\\left(\\begin{array}{cc|c}1&2&3\\\\2&4&7\\end{array}\\right)$.", why: "Standard setup." },
      { text: "$R_2\\to R_2-2R_1$ gives $(0,0\\mid 1)$.", why: "The left side vanishes." },
      { text: "That row reads $0x+0y=1$.", why: "Translate back to an equation." },
      { text: "No pair $(x,y)$ can satisfy $0=1$.", why: "Contradiction." },
      { text: "So the system is inconsistent: no solution.", why: "Definition." },
      { text: "Rank of the coefficient matrix is $1$; rank of the augmented matrix is $2$.", why: "Ranks disagree." },
      { text: "Rouché–Capelli: consistency requires equal ranks.", why: "The formal criterion." },
      { text: "Geometrically the lines $y=(3-x)/2$ and $y=(7-2x)/4$ are parallel with different intercepts.", why: "Picture matches the algebra." },
    ],
    result: "No solution — the system is inconsistent",
    check: "Slopes are both $-1/2$; intercepts $1.5$ and $1.75$ differ.",
    mistake: "Reporting $(0,0)$ as a solution because the left side became zero.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Infinitely many solutions with one free variable",
    setup: "Solve $x+y+z=6$, $2x+3y+z=14$, $3x+4y+2z=20$.",
    steps: [
      { text: "Augment: $\\left(\\begin{array}{ccc|c}1&1&1&6\\\\2&3&1&14\\\\3&4&2&20\\end{array}\\right)$.", why: "Setup." },
      { text: "$R_2-2R_1=(0,1,-1\\mid 2)$.", why: "Clear column $1$." },
      { text: "$R_3-3R_1=(0,1,-1\\mid 2)$.", why: "Same row appears twice." },
      { text: "$R_3-R_2=(0,0,0\\mid 0)$, a harmless zero row.", why: "Redundant equation, not a contradiction." },
      { text: "Pivots in columns $1$ and $2$, so $z$ is free: set $z=t$.", why: "Parameterize the free variable." },
      { text: "Row $2$: $y-z=2\\Rightarrow y=2+t$.", why: "Back-substitute." },
      { text: "Row $1$: $x=6-y-z=6-(2+t)-t=4-2t$.", why: "Continue upward." },
      { text: "Solution set $(x,y,z)=(4,2,0)+t(-2,1,1)$, a line in $\\mathbb{R}^3$.", why: "Particular solution plus nullspace direction." },
    ],
    result: "$(x,y,z)=(4-2t,\\;2+t,\\;t)$ for all $t\\in\\mathbb{R}$",
    check: "$t=1$ gives $(2,3,1)$: $2+3+1=6$ and $2(2)+3(3)+1=14$. Correct.",
    mistake: "Declaring \"no solution\" when a row of all zeros (including the constant) appears.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Reduced row echelon form",
    setup: "Bring $\\left(\\begin{array}{ccc|c}2&4&-2&2\\\\1&3&1&5\\\\0&1&2&4\\end{array}\\right)$ to RREF and solve.",
    steps: [
      { text: "Scale $R_1$ by $\\tfrac12$: $(1,2,-1\\mid 1)$.", why: "Make the leading pivot $1$." },
      { text: "$R_2\\to R_2-R_1$: $(0,1,2\\mid 4)$.", why: "Clear below the first pivot." },
      { text: "$R_3-R_2=(0,0,0\\mid 0)$, so row $3$ was redundant.", why: "Identical rows collapse." },
      { text: "$R_1\\to R_1-2R_2$: $(1,0,-5\\mid -7)$.", why: "Clear above the second pivot for RREF." },
      { text: "RREF is $\\left(\\begin{array}{ccc|c}1&0&-5&-7\\\\0&1&2&4\\\\0&0&0&0\\end{array}\\right)$.", why: "Pivots are $1$ with zeros above and below." },
      { text: "Free variable $z=t$.", why: "Column $3$ has no pivot." },
      { text: "$x=-7+5t$ and $y=4-2t$.", why: "Read the pivot rows directly." },
      { text: "Solution: $(-7,4,0)+t(5,-2,1)$.", why: "Line of solutions." },
    ],
    result: "$(x,y,z)=(-7+5t,\\;4-2t,\\;t)$",
    check: "$t=2$ gives $(3,0,2)$: $2(3)+4(0)-2(2)=2$. Correct.",
    mistake: "Stopping at echelon form and forgetting to clear entries above the pivots.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Parameter decides the solution type",
    setup: "For which $k$ does $x+y=2$, $2x+ky=4$ have a unique solution, infinitely many, or none?",
    steps: [
      { text: "Coefficient determinant: $\\det\\begin{pmatrix}1&1\\\\2&k\\end{pmatrix}=k-2$.", why: "Determinant controls uniqueness." },
      { text: "If $k\\ne2$ then $\\det\\ne0$ and the solution is unique.", why: "Invertible coefficient matrix." },
      { text: "Solve for that case: from row $1$, $y=2-x$.", why: "Substitution." },
      { text: "Row $2$: $2x+k(2-x)=4\\Rightarrow x(2-k)=4-2k$.", why: "Collect $x$." },
      { text: "So $x=\\dfrac{4-2k}{2-k}=2$ and $y=0$ for every $k\\ne2$.", why: "The factor $2-k$ cancels." },
      { text: "If $k=2$, row $2$ becomes $2x+2y=4$, i.e. exactly $2\\times$ row $1$.", why: "Duplicate equation." },
      { text: "Then the system reduces to $x+y=2$: infinitely many solutions $(t,2-t)$.", why: "One free variable." },
      { text: "No value of $k$ makes it inconsistent, because the right sides scale in the same ratio.", why: "Rank of the augmented matrix never exceeds rank of the coefficient matrix here." },
    ],
    result: "Unique $(2,0)$ for $k\\ne2$; infinitely many $(t,2-t)$ when $k=2$; never inconsistent",
    check: "$k=5$: $2(2)+5(0)=4$. Correct.",
    mistake: "Dividing by $2-k$ without separating the case $k=2$.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Three planes meeting at a point",
    setup: "Solve $x+2y+3z=9$, $2x-y+z=8$, $3x+z=7$.",
    steps: [
      { text: "Augment and eliminate: $R_2-2R_1=(0,-5,-5\\mid -10)$.", why: "Clear $x$ from row $2$." },
      { text: "Divide by $-5$: $(0,1,1\\mid 2)$.", why: "Simplify to a clean pivot row." },
      { text: "$R_3-3R_1=(0,-6,-8\\mid -20)$.", why: "Clear $x$ from row $3$." },
      { text: "Divide by $-2$: $(0,3,4\\mid 10)$.", why: "Reduce the numbers." },
      { text: "$R_3-3R_2=(0,0,1\\mid 4)$, so $z=4$.", why: "Third pivot found." },
      { text: "Row $2$: $y+z=2\\Rightarrow y=-2$.", why: "Back-substitute $z$." },
      { text: "Row $1$: $x=9-2(-2)-3(4)=9+4-12=1$.", why: "Back-substitute both." },
      { text: "Three pivots for three unknowns, so the planes meet in the single point $(1,-2,4)$.", why: "Unique solution." },
    ],
    result: "$(x,y,z)=(1,-2,4)$",
    check: "Equation $3$: $3(1)+4=7$. Correct.",
    mistake: "Losing a sign when dividing a row by a negative number.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Homogeneous system and its nullspace",
    setup: "Solve $x+2y-z=0$, $2x+4y-2z=0$, $-x-2y+z=0$.",
    steps: [
      { text: "Homogeneous systems always have the trivial solution $(0,0,0)$.", why: "So the only question is whether more exist." },
      { text: "Row $2$ is $2\\times$ row $1$; row $3$ is $-1\\times$ row $1$.", why: "Spot the multiples immediately." },
      { text: "Elimination leaves a single independent equation $x+2y-z=0$.", why: "Rank $=1$." },
      { text: "Nullity $=3-1=2$, so there are two free variables.", why: "Rank–nullity." },
      { text: "Let $y=s$, $z=t$; then $x=-2s+t$.", why: "Solve for the pivot variable." },
      { text: "Solution: $(x,y,z)=s(-2,1,0)+t(1,0,1)$.", why: "Parameterized nullspace." },
      { text: "Basis of the nullspace: $\\{(-2,1,0),(1,0,1)\\}$; the solution set is a plane through the origin.", why: "Two independent directions." },
      { text: "Both basis vectors satisfy the equation: $-2+2-0=0$ and $1+0-1=0$.", why: "Verification." },
    ],
    result: "Nullspace $=\\mathrm{Span}\\{(-2,1,0),(1,0,1)\\}$, a plane of solutions",
    check: "$s=1,t=1$ gives $(-1,1,1)$: $-1+2-1=0$. Correct.",
    mistake: "Reporting only $(0,0,0)$ when the rank is less than the number of unknowns.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Balancing a chemical equation with elimination",
    setup: "Balance $a\\,\\mathrm{C_3H_8}+b\\,\\mathrm{O_2}\\to c\\,\\mathrm{CO_2}+d\\,\\mathrm{H_2O}$.",
    steps: [
      { text: "Carbon: $3a=c$.", why: "Atom counts give one equation per element." },
      { text: "Hydrogen: $8a=2d$, so $d=4a$.", why: "Propane has $8$ hydrogens; water has $2$." },
      { text: "Oxygen: $2b=2c+d$.", why: "Oxygen appears in both products." },
      { text: "Substitute $c=3a$ and $d=4a$: $2b=6a+4a=10a$.", why: "Reduce to one unknown." },
      { text: "So $b=5a$.", why: "Divide by $2$." },
      { text: "This homogeneous system has one free parameter $a$: solutions are $a(1,5,3,4)$.", why: "Rank $3$, four unknowns." },
      { text: "Choose the smallest positive integers by taking $a=1$.", why: "Chemistry wants integer coefficients." },
      { text: "$\\mathrm{C_3H_8}+5\\mathrm{O_2}\\to3\\mathrm{CO_2}+4\\mathrm{H_2O}$.", why: "Balanced equation." },
    ],
    result: "$(a,b,c,d)=(1,5,3,4)$",
    check: "Oxygen: left $10$, right $3(2)+4(1)=10$. Balanced.",
    mistake: "Trying to force a unique solution; the system is homogeneous, so only the ratio is determined.",
  },
];

export const LA_SYSTEMS_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Rank from echelon form",
    setup: "Find the rank of $A=\\begin{pmatrix}1&3&2\\\\2&6&5\\\\1&3&3\\end{pmatrix}$.",
    steps: [
      { text: "$R_2-2R_1=(0,0,1)$.", why: "Eliminate the first column." },
      { text: "$R_3-R_1=(0,0,1)$.", why: "Same operation on row $3$." },
      { text: "$R_3-R_2=(0,0,0)$.", why: "Rows $2$ and $3$ were identical after step one." },
      { text: "Echelon form: $\\begin{pmatrix}1&3&2\\\\0&0&1\\\\0&0&0\\end{pmatrix}$.", why: "Two nonzero rows." },
      { text: "Pivots sit in columns $1$ and $3$.", why: "Column $2$ has no pivot." },
      { text: "So $\\mathrm{rank}(A)=2$.", why: "Rank counts pivots." },
      { text: "Column $2$ equals $3\\times$ column $1$, which is the dependence relation.", why: "Explains the missing pivot." },
      { text: "Since rank $<3$, $A$ is singular and $\\det A=0$.", why: "Consistent with a zero row." },
    ],
    result: "$\\mathrm{rank}(A)=2$, so $A$ is singular",
    check: "Nullity $=3-2=1$, and indeed $A(3,-1,0)^{T}=0$.",
    mistake: "Counting nonzero rows of the original matrix instead of the echelon form.",
    life: "In survey data, rank tells you how many questions actually carry independent information — duplicated questions show up as missing pivots.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Consistency test by comparing ranks",
    setup: "Is $x+y+z=1$, $2x+2y+2z=2$, $x+y+z=3$ consistent?",
    steps: [
      { text: "Coefficient matrix has all rows proportional to $(1,1,1)$, so its rank is $1$.", why: "One independent direction." },
      { text: "Augmented matrix: rows $(1,1,1\\mid1)$, $(2,2,2\\mid2)$, $(1,1,1\\mid3)$.", why: "Include the constants." },
      { text: "$R_2-2R_1=(0,0,0\\mid0)$: no information.", why: "Row $2$ was a multiple of row $1$." },
      { text: "$R_3-R_1=(0,0,0\\mid2)$.", why: "This is the decisive row." },
      { text: "That row says $0=2$, a contradiction.", why: "Impossible equation." },
      { text: "Rank of the augmented matrix is $2$, but rank of the coefficient matrix is $1$.", why: "Ranks differ." },
      { text: "Rouché–Capelli: unequal ranks means no solution.", why: "Formal criterion." },
      { text: "Geometrically, equations $1$ and $3$ are parallel planes that never meet.", why: "Picture." },
    ],
    result: "Inconsistent — no solution",
    check: "Equations $1$ and $3$ demand $x+y+z$ to be both $1$ and $3$.",
    mistake: "Ignoring the augmented column when computing rank.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Nullspace basis for a wide matrix",
    setup: "Find a basis for the nullspace of $A=\\begin{pmatrix}1&2&1&0\\\\0&1&3&1\\end{pmatrix}$.",
    steps: [
      { text: "$A$ is already in echelon form with pivots in columns $1$ and $2$.", why: "Read pivots off." },
      { text: "Clear above the second pivot: $R_1-2R_2=(1,0,-5,-2)$.", why: "Get RREF." },
      { text: "Pivot equations: $x_1-5x_3-2x_4=0$ and $x_2+3x_3+x_4=0$.", why: "Translate rows." },
      { text: "Free variables are $x_3$ and $x_4$, so nullity $=4-2=2$.", why: "Rank–nullity." },
      { text: "Set $x_3=1,x_4=0$: $x_2=-3$, $x_1=5$, giving $(5,-3,1,0)$.", why: "First basis vector." },
      { text: "Set $x_3=0,x_4=1$: $x_2=-1$, $x_1=2$, giving $(2,-1,0,1)$.", why: "Second basis vector." },
      { text: "These two are independent because of the identity pattern in the free coordinates.", why: "Standard argument." },
      { text: "Nullspace $=\\mathrm{Span}\\{(5,-3,1,0),(2,-1,0,1)\\}$.", why: "Basis complete." },
    ],
    result: "Basis $\\{(5,-3,1,0),(2,-1,0,1)\\}$",
    check: "$A(5,-3,1,0)^{T}=(5-6+1,\\,-3+3)=(0,0)$.",
    mistake: "Forgetting to clear above the pivot, which produces wrong pivot-variable formulas.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "General solution as particular plus homogeneous",
    setup: "Solve $x+2y+z=4$, $2x+4y+3z=11$ and write the answer in vector form.",
    steps: [
      { text: "$R_2-2R_1=(0,0,1\\mid3)$, so $z=3$.", why: "Elimination finds a pivot in column $3$." },
      { text: "Pivots are in columns $1$ and $3$; $y$ is free.", why: "Column $2$ has no pivot." },
      { text: "Row $1$: $x=4-2y-z=4-2y-3=1-2y$.", why: "Back-substitute $z=3$." },
      { text: "Let $y=t$: $(x,y,z)=(1-2t,\\;t,\\;3)$.", why: "Parameterize." },
      { text: "Vector form: $(1,0,3)+t(-2,1,0)$.", why: "Particular solution plus a direction." },
      { text: "The direction $(-2,1,0)$ satisfies the homogeneous system: $-2+2+0=0$.", why: "It is a nullspace vector." },
      { text: "So the solution set is a line, and its structure is $x_p+\\mathcal{N}(A)$.", why: "General theory of linear systems." },
      { text: "Uniqueness fails precisely because the nullspace is nontrivial.", why: "Links geometry to rank." },
    ],
    result: "$(x,y,z)=(1,0,3)+t(-2,1,0)$",
    check: "$t=2$ gives $(-3,2,3)$: $-3+4+3=4$ and $-6+8+9=11$.",
    mistake: "Reporting only the particular solution $(1,0,3)$.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Column space versus nullspace dimensions",
    setup: "For $A=\\begin{pmatrix}1&2&3\\\\2&4&6\\\\1&2&3\\end{pmatrix}$, describe $\\mathrm{Col}(A)$, $\\mathcal{N}(A)$, and the row space.",
    steps: [
      { text: "All rows are multiples of $(1,2,3)$, so $\\mathrm{rank}(A)=1$.", why: "Row space is one-dimensional." },
      { text: "$\\mathrm{Col}(A)$ is spanned by the first column $(1,2,1)$.", why: "The other columns are multiples of it." },
      { text: "Indeed column $2=2\\times$ column $1$ and column $3=3\\times$ column $1$.", why: "Explicit dependence." },
      { text: "$\\dim\\mathrm{Col}(A)=1$: a line through the origin in $\\mathbb{R}^3$.", why: "Rank equals column-space dimension." },
      { text: "Nullity $=3-1=2$.", why: "Rank–nullity with $n=3$ columns." },
      { text: "The one pivot equation is $x+2y+3z=0$.", why: "From the single independent row." },
      { text: "Nullspace basis: $(-2,1,0)$ and $(-3,0,1)$.", why: "Set $(y,z)=(1,0)$ then $(0,1)$." },
      { text: "Row space $=\\mathrm{Span}\\{(1,2,3)\\}$, and it is orthogonal to the nullspace.", why: "Row space and nullspace are orthogonal complements." },
    ],
    result: "$\\mathrm{rank}=1$: $\\mathrm{Col}(A)=\\mathrm{Span}\\{(1,2,1)\\}$, $\\mathcal{N}(A)=\\mathrm{Span}\\{(-2,1,0),(-3,0,1)\\}$",
    check: "$(1,2,3)\\cdot(-2,1,0)=0$ and $(1,2,3)\\cdot(-3,0,1)=0$.",
    mistake: "Assuming the column space lives in the same copy of $\\mathbb{R}^n$ as the nullspace when the matrix is not square.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Parameter changes the rank",
    setup: "Discuss the solutions of $x+y+z=1$, $x+2y+kz=2$, $x+ky+2z=3$ as $k$ varies.",
    steps: [
      { text: "$R_2-R_1=(0,1,k-1\\mid1)$.", why: "Eliminate $x$." },
      { text: "$R_3-R_1=(0,k-1,1\\mid2)$.", why: "Same for row $3$." },
      { text: "$R_3-(k-1)R_2$: third entry $1-(k-1)^{2}$, constant $2-(k-1)$.", why: "Eliminate $y$." },
      { text: "So the last row is $\\bigl(0,0,\\;1-(k-1)^{2}\\;\\bigm|\\;3-k\\bigr)$.", why: "Both sides simplified." },
      { text: "$1-(k-1)^{2}=(2-k)k$, which vanishes when $k=0$ or $k=2$.", why: "Factor the difference of squares." },
      { text: "If $k\\ne0,2$: three pivots, unique solution with $z=\\dfrac{3-k}{k(2-k)}$.", why: "Nonzero pivot." },
      { text: "If $k=2$: last row is $(0,0,0\\mid1)$ — inconsistent, no solution.", why: "$3-k=1\\ne0$." },
      { text: "If $k=0$: last row is $(0,0,0\\mid3)$ — also inconsistent.", why: "$3-k=3\\ne0$." },
    ],
    result: "Unique solution for $k\\notin\\{0,2\\}$; no solution for $k=0$ or $k=2$",
    check: "$k=1$: $z=2/1=2$, then $y=1-0=1$... substituting back gives $x+y+z=1$ with $(x,y,z)=(-2,1,2)$. Correct.",
    mistake: "Concluding \"infinitely many solutions\" whenever a pivot vanishes; you must also check the constant.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Least-squares solution of an inconsistent system",
    setup: "The system $x=1$, $x=2$, $x=4$ has no exact solution; find the least-squares $x$.",
    steps: [
      { text: "Matrix form: $A=(1,1,1)^{T}$, $b=(1,2,4)^{T}$.", why: "Three equations, one unknown." },
      { text: "Rank of $A$ is $1$, rank of $[A\\mid b]$ is $2$, so no exact solution exists.", why: "Confirms inconsistency." },
      { text: "Normal equations: $A^{T}Ax=A^{T}b$.", why: "Least squares minimizes $\\lVert Ax-b\\rVert$." },
      { text: "$A^{T}A=1+1+1=3$.", why: "Sum of squares of the column." },
      { text: "$A^{T}b=1+2+4=7$.", why: "Dot product of the column with $b$." },
      { text: "So $3x=7$ and $x=\\tfrac73\\approx2.333$.", why: "Solve the scalar normal equation." },
      { text: "Residuals: $1-\\tfrac73=-\\tfrac43$, $2-\\tfrac73=-\\tfrac13$, $4-\\tfrac73=\\tfrac53$.", why: "Check the errors." },
      { text: "They sum to zero, which is exactly the orthogonality condition $A^{T}(b-Ax)=0$.", why: "Geometric meaning of least squares." },
    ],
    result: "$x=\\tfrac73$ — the mean of the measurements",
    check: "$\\lVert r\\rVert^{2}=\\tfrac{16+1+25}{9}=\\tfrac{42}{9}$, smaller than at $x=2$ (which gives $5$).",
    mistake: "Trying to force an exact solution instead of minimizing the residual.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Network flow as a linear system",
    setup: "At a junction, inflows $x_1,x_2$ and outflows $x_3,x_4$ satisfy $x_1+x_2=x_3+x_4$, $x_1-x_3=10$, $x_2-x_4=-10$. Describe all flows.",
    steps: [
      { text: "Rewrite: $x_1+x_2-x_3-x_4=0$, $x_1-x_3=10$, $x_2-x_4=-10$.", why: "Standard form." },
      { text: "Add the last two equations: $x_1+x_2-x_3-x_4=0$.", why: "That reproduces the first equation." },
      { text: "So equation $1$ is redundant: rank $=2$ with four unknowns.", why: "Conservation is implied by the branch balances." },
      { text: "Nullity $=4-2=2$: two free variables.", why: "Rank–nullity." },
      { text: "Let $x_3=s$ and $x_4=t$.", why: "Choose the non-pivot columns." },
      { text: "Then $x_1=10+s$ and $x_2=t-10$.", why: "Solve the pivot equations." },
      { text: "General solution: $(10,-10,0,0)+s(1,0,1,0)+t(0,1,0,1)$.", why: "Particular plus nullspace." },
      { text: "Physical flows need $x_i\\ge0$, forcing $s\\ge0$ and $t\\ge10$.", why: "The model adds inequality constraints on top of the algebra." },
    ],
    result: "$(x_1,x_2,x_3,x_4)=(10+s,\\;t-10,\\;s,\\;t)$ with $s\\ge0$, $t\\ge10$",
    check: "$s=0,t=10$: $(10,0,0,10)$ balances $10+0=0+10$.",
    mistake: "Ignoring nonnegativity and reporting flows that run backwards through a one-way pipe.",
  },
];
